import { Notification } from "@/components/admin/notification";
import { TextInput } from "@/components/admin/text-input";
import { useConfigurationContext } from "@/components/gsw-erp/root/ConfigurationContext.tsx";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Form, required, useLogin, useNotify } from "ra-core";
import { useEffect, useRef, useState } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { SSOAuthButton } from "./SSOAuthButton";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const {
    title,
    googleWorkplaceDomain,
    disableEmailPasswordAuthentication,
  } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const [isInitializing] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify(
      "If you're a registered user, you should receive a password recovery email shortly.",
      {
        type: "success",
      },
    );

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden h-full flex-col bg-slate-50 p-10 overflow-hidden lg:flex">
          {/* Multi-color abstract shapes left side */}
          <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-gradient-to-tr from-purple-200/40 via-blue-200/40 to-transparent rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-gradient-to-bl from-orange-200/40 via-yellow-200/40 to-transparent rounded-full blur-[80px] pointer-events-none" />
          {/* Subtle grid texture overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

          {/* Rovasys Logo Top Center in left side */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-full flex justify-center pointer-events-none">
             <img className="h-20 md:h-28 lg:h-36 xl:h-44 object-contain drop-shadow-2xl select-none" src="/logos/rovasys_horizontal.png" alt="Rovasys Logo" draggable={false} />
          </div>

          <div className="relative z-20 flex items-center justify-between mt-28 lg:mt-32">
            <div className="flex items-center text-xl font-bold text-slate-800 invisible">
              {title}
            </div>
          </div>
          <div className="relative z-20 flex flex-col items-center justify-center flex-1 w-full mt-2">
            <img 
              src="/login-illustration.png" 
              alt="Team Collaboration" 
              className="w-[85%] max-w-lg object-contain drop-shadow-2xl pointer-events-none select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="text-center mt-6 space-y-3 max-w-md">
              <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
                Streamline Your Operations
              </h2>
              <p className="text-slate-600 text-base leading-relaxed">
                Connect your team, automate workflows, and gain deep insights into your business with GSW ERP.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center w-full p-4 lg:p-12 relative overflow-hidden bg-white">
          {/* Multi-color abstract textures right side */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-teal-100/50 via-emerald-50/30 to-transparent rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-100/50 via-indigo-50/30 to-transparent rounded-full blur-[100px] pointer-events-none translate-y-1/2 -translate-x-1/4" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
          
          <div className="w-full space-y-8 lg:mx-auto lg:w-[420px] relative z-10 bg-white/80 backdrop-blur-xl p-8 sm:p-10 rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-white/50 ring-1 ring-slate-100">
            {isInitializing ? (
              <div className="space-y-8 animate-in fade-in duration-500">
                <div className="space-y-3 flex flex-col items-center">
                  <Skeleton className="h-10 w-48 rounded-lg bg-slate-200/50" />
                  <Skeleton className="h-4 w-64 rounded-md bg-slate-100" />
                </div>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 rounded-md bg-slate-100" />
                    <Skeleton className="h-14 w-full rounded-2xl bg-slate-100/80" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 rounded-md bg-slate-100" />
                    <Skeleton className="h-14 w-full rounded-2xl bg-slate-100/80" />
                  </div>
                  <div className="flex justify-between pt-2">
                    <Skeleton className="h-4 w-28 rounded-md bg-slate-100" />
                    <Skeleton className="h-4 w-32 rounded-md bg-slate-100" />
                  </div>
                  <Skeleton className="h-14 w-full rounded-2xl bg-slate-200/60 mt-4" />
                </div>
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="space-y-2 text-center mb-8">
                  <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
                  <p className="text-sm font-medium text-slate-500">Please enter your details to sign in.</p>
                </div>
                {disableEmailPasswordAuthentication ? null : (
                  <Form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-6">
                      <div className="relative group">
                        <TextInput
                          label="Email Address"
                          source="email"
                          type="email"
                          className="w-full font-medium [&_label]:text-slate-700 [&_label]:font-semibold [&_input]:h-[52px] [&_input]:rounded-2xl [&_input]:bg-white [&_input]:border [&_input]:border-slate-300 [&_input]:shadow-sm hover:[&_input]:border-primary/50 focus-visible:[&_input]:border-primary focus-visible:[&_input]:ring-4 focus-visible:[&_input]:ring-primary/10 [&_input]:transition-all [&_input]:text-slate-900 [&_input]:px-4"
                          validate={required()}
                        />
                      </div>
                      <div className="relative group">
                        <TextInput
                          label="Password"
                          source="password"
                          type="password"
                          className="w-full font-medium [&_label]:text-slate-700 [&_label]:font-semibold [&_input]:h-[52px] [&_input]:rounded-2xl [&_input]:bg-white [&_input]:border [&_input]:border-slate-300 [&_input]:shadow-sm hover:[&_input]:border-primary/50 focus-visible:[&_input]:border-primary focus-visible:[&_input]:ring-4 focus-visible:[&_input]:ring-primary/10 [&_input]:transition-all [&_input]:text-slate-900 [&_input]:px-4"
                          validate={required()}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1 pb-2">
                      <div className="flex items-center">
                         <input id="remember-me" name="remember-me" type="checkbox" className="h-[18px] w-[18px] text-primary focus:ring-primary/20 border-slate-300 rounded cursor-pointer transition-colors" />
                         <label htmlFor="remember-me" className="ml-2.5 block text-sm font-medium text-slate-600 cursor-pointer select-none">
                           Remember me
                         </label>
                      </div>
                      {disableEmailPasswordAuthentication ? null : (
                        <Link
                          to={"/forgot-password"}
                          className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                        >
                          Forgot password?
                        </Link>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-[52px] rounded-2xl text-[15px] font-bold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all bg-gradient-to-r from-primary to-indigo-600 hover:opacity-95 active:scale-[0.98]"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-[2.5px] border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        "Sign in to Dashboard"
                      )}
                    </Button>
                  </Form>
                )}
                {googleWorkplaceDomain ? (
                  <div className="mt-8">

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-white font-medium text-slate-400">Or continue with</span>
                      </div>
                    </div>
                    <div className="mt-6">
                      <SSOAuthButton className="w-full h-[52px] rounded-2xl border-2 border-slate-200 font-bold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all" domain={googleWorkplaceDomain}>
                         Google Workplace
                      </SSOAuthButton>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
